import * as actionTypes from "./actionTypes";
import api, { baseURL } from "../../conf/api.js";
import axios from "axios";
import Cookies from "js-cookie";
import jwt_decode from "jwt-decode";
import { forgetCaptionPrompt } from "@wooglobe/vre-editor/core";
import { toast } from "react-toastify";

export const authStart = () => {
  return {
    type: actionTypes.AUTH_START,
  };
};

export const authSuccess = (data) => {
  return {
    type: actionTypes.AUTH_SUCCESS,
    access_token: data.access,
    refresh_token: data.refresh,
  };
};

export const authFail = (error) => {
  Cookies.remove("access_token");
  Cookies.remove("refresh_token");
  return {
    type: actionTypes.AUTH_FAIL,
    error,
  };
};

export const authLogout = () => {
  Cookies.remove("access_token");
  Cookies.remove("refresh_token");
  // The video editor keeps a per-user caption prompt in this browser. It is
  // meant to last only as long as the session, so signing out forgets it and
  // the next login starts from the product's own default again. Called with no
  // identity on purpose: by this point the token it would come from is gone,
  // and "forget every one of them" is the right reading of a sign-out.
  forgetCaptionPrompt();
  return {
    type: actionTypes.AUTH_LOGOUT,
  };
};

export const getRefreshToken = async (refreshToken) => {
  const refresh_token = refreshToken;
  const response = await axios
    .post(baseURL + "/auth/token/refresh/", {
      refresh: refresh_token,
    })
    .catch((res) => {
      console.log("Refresh Token", res);
    });
  return response?.data ? response?.data : null;
};

export const isAccessTokenExpired = (accessToken) => {
  try {
    const decodedToken = jwt_decode(accessToken);
    const currentTime = Math.floor(Date.now() / 1000);
    return decodedToken.exp < currentTime;
  } catch (err) {
    return true;
  }
};

export const authLogin = (email, password) => {
  return async (dispatch) => {
    dispatch(authStart());
    axios
      .post(baseURL + "/auth/token/", { email: email, password: password })
      .then((response) => {
        if (response.status === 200) {
          dispatch(authSuccess(response.data));
        }
      })
      .catch((error) => {
        try {
          if (error.response?.data?.detail) {
            toast.error(error.response.data?.detail);
            dispatch(authFail(error.response?.data?.detai));
          }
          else {
            toast.error("Something went wrong!");
            dispatch(authFail("Something went wrong"));
          }
        } catch {
          toast.error("Something went wrong!");
          dispatch(authFail(error.status));
        }
      });
  };
};

export const authSignup = (data, afterSuccess, onError = (error) => { }) => {
  return async (dispatch) => {
    await api
      .post("/auth/register/", data, { timeout: 10000 * 60 * 1 })
      .then((response) => {
        if (response.status === 201) {
          toast.success(response.data?.message);
          if (response.data?.warning) {
            toast.warning(response.data.warning);
          }
          afterSuccess();
        }
      })
      .catch((error) => {
        onError(error.response);
        const errorObject = error.response?.data?.message;
        try {
          for (const field in errorObject) {
            if (errorObject?.hasOwnProperty(field)) {
              const errors = errorObject[field];
              const formattedFieldErrors = errors.map(
                (errorMessage) => `${field}:${errorMessage}`
              );
              formattedFieldErrors?.map((msg) => toast.error(msg));
            }
          }
        } catch {
          try {
            toast.error(JSON.stringify(errorObject));
          } catch {
            toast.error("Something went wrong");
          }
        }
      });
  };
};

export const authCheckState = () => {
  return async (dispatch) => {
    const accessToken = Cookies.get("access_token");
    const refreshToken = Cookies.get("refresh_token");
    try {
      if (
        !accessToken ||
        accessToken === "undefined" ||
        refreshToken === "undefined" ||
        !refreshToken
      ) {
        dispatch(authFail("Cookies is not authenticated"));
        if (
          window.location.pathname !== "/" &&
          window.location.pathname !== "/terms" &&
          window.location.pathname !== "/creator_signup" &&
          window.location.pathname !== "/partner_signup169"
        ) {
          window.location.replace("/");
        }
        return;
      }
      if (isAccessTokenExpired(accessToken)) {
        const response = await getRefreshToken(refreshToken);
        dispatch(authSuccess(response));
      } else {
        dispatch(authSuccess({ access: accessToken, refresh: refreshToken }));
      }
    } catch { }
  };
};

export const ghostLogin = (targetUserId, onSuccess, onError) => {
  return async (dispatch) => {
    dispatch(authStart());
    try {
      const response = await api.post("/auth/ghost-login/", {
        target_user_id: targetUserId,
      });

      if (response.status === 200) {
        const { access, refresh, user, ghost_login, original_superuser } = response.data;

        // Store original superuser info if needed
        if (ghost_login) {
          sessionStorage.setItem("ghost_login", "true");
          sessionStorage.setItem("original_superuser", original_superuser);
        }

        // Authenticate as the target user
        dispatch(authSuccess({ access, refresh }));

        toast.success(`Ghost login successful! Logged in as ${user.email}`);

        if (onSuccess) {
          onSuccess(user);
        }
      }
    } catch (error) {
      const errorMessage = error.response?.data?.detail ||
                          error.response?.data?.target_user_id?.[0] ||
                          "Ghost login failed";
      toast.error(errorMessage);
      dispatch(authFail(errorMessage));

      if (onError) {
        onError(error);
      }
    }
  };
};
